// Wave 20.49cp — Mapbox Static Images URL for a route polyline.
//
// Extracted for the share card so it doesn't import from a page
// module. Encoder matches web/src/Feed.tsx#encodePolyline and
// mobile/src/components/StaticRouteImage.tsx byte-for-byte.

import { colors } from '../theme';

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

export type LatLng = { lat: number; lng: number };

export function encodePolyline(points: LatLng[]): string {
  let lastLat = 0;
  let lastLng = 0;
  let out = '';
  const encode = (n: number): string => {
    let v = n < 0 ? ~(n << 1) : n << 1;
    let s = '';
    while (v >= 0x20) {
      s += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
      v >>>= 5;
    }
    s += String.fromCharCode(v + 63);
    return s;
  };
  for (const p of points) {
    const lat = Math.round(p.lat * 1e5);
    const lng = Math.round(p.lng * 1e5);
    out += encode(lat - lastLat);
    out += encode(lng - lastLng);
    lastLat = lat;
    lastLng = lng;
  }
  return out;
}

export function decimate<T>(pts: T[], maxPoints: number): T[] {
  if (pts.length <= maxPoints) return pts;
  const step = Math.ceil(pts.length / maxPoints);
  const out: T[] = [];
  for (let i = 0; i < pts.length; i += step) out.push(pts[i]);
  if (out[out.length - 1] !== pts[pts.length - 1]) out.push(pts[pts.length - 1]);
  return out;
}

/** Route-only static image URL. `null` entries in `points` are
 *  privacy-zone breaks (rendered as disconnected segments). `size`
 *  is Mapbox's `{w}x{h}[@2x]` spec, each side ≤ 1280. Returns null
 *  when there's nothing to draw. */
export function buildStaticRouteUrl(
  points: Array<LatLng | null>,
  style = 'dark-v11',
  size = '1280x720@2x',
  padding = 80,
  // Wave 20.49cr — numbered peak pins. `label` must be a single
  // alphanumeric (Mapbox `pin-s-{label}`); we pass '1'..'9'.
  markers: Array<LatLng & { label: string }> = [],
): string | null {
  if (!MAPBOX_TOKEN) return null;
  if (points.filter((p) => p != null).length < 2) return null;
  const strokeHex = (colors.brand || '#6AFF45').replace('#', '').toLowerCase();
  const segments: LatLng[][] = [[]];
  for (const p of points) {
    if (p === null) {
      if (segments[segments.length - 1].length > 0) segments.push([]);
    } else {
      segments[segments.length - 1].push(p);
    }
  }
  const paths = segments
    .filter((s) => s.length >= 2)
    .map((s) => `path-3+${strokeHex}-0.9(${encodeURIComponent(encodePolyline(decimate(s, 200)))})`);
  if (paths.length === 0) return null;
  const pins = markers
    .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng) && /^[a-z0-9]$/i.test(m.label))
    .map((m) => `pin-s-${m.label.toLowerCase()}+${strokeHex}(${m.lng.toFixed(5)},${m.lat.toFixed(5)})`);
  return (
    `https://api.mapbox.com/styles/v1/mapbox/${style}/static/` +
    `${[...paths, ...pins].join(',')}/auto/${size}` +
    `?access_token=${MAPBOX_TOKEN}&padding=${padding}&logo=false&attribution=false`
  );
}
